import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { AuthEditorialPanel } from "@/components/auth/AuthEditorialPanel";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { AuthFields } from "@/components/auth/AuthFields";

const EMPTY_FORM = { name: "", email: "", password: "", accepted_terms: false };

export default function AuthForm({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && !form.accepted_terms) {
      setError("Please tick the box to agree to the Terms and Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password, form.accepted_terms);
      toast.success(isLogin ? "Welcome back." : "Account created. Welcome to the doctrine.");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <AuthEditorialPanel />
      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.65, 0, 0.35, 1] }}
          className="w-full max-w-md pt-24 lg:pt-0"
        >
          <div className="section-tag mb-4">{isLogin ? "Welcome back" : "Create your account"}</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-8">
            {isLogin ? "Sign in." : "Join T.A.C.C."}
          </h2>

          <GoogleSignInButton showTermsNote={!isLogin} />

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            <AuthFields form={form} setForm={setForm} isLogin={isLogin} />
            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
            <button type="submit" disabled={loading} data-testid="auth-submit" className="btn-ember text-sm w-full justify-center">
              {loading ? "Please wait…" : isLogin ? "Sign In" : "Create Account"}
              <ArrowUpRight size={16} />
            </button>
          </form>

          {isLogin && (
            <div className="mt-4 text-right">
              <Link to="/forgot-password" className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] hover:text-[#B45309] transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          )}

          <p className="mt-8 text-sm text-[#78716C]">
            {isLogin ? "New here? " : "Already a member? "}
            <Link to={isLogin ? "/register" : "/login"} className="tacc-link text-[#B45309]" data-testid="auth-switch">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
          <Link to="/" className="mt-4 inline-block font-mono text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] hover:text-[#B45309] transition-colors">← Back to site</Link>
        </motion.div>
      </div>
    </main>
  );
}
